import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
import {
	Form,
	FormGroup,
	Modal,
	ModalBody,
	ModalFooter
} from 'reactstrap';

import { FormField, FIELD_TYPES } from '@techchange/components';
import { Button, DangerAlert, WarningAlert } from '@techchange/components';
import { messages } from '../../i18n';

const propTypes = {

	/**
	 * The title of the object to delete.
	 */
	title: PropTypes.string,

	/**
	 * The type of object to delete.
	 */
	type: PropTypes.string,

	/**
	 * A custom react-intl message object (if any) to display as the modal header.
	 */
	headerLabel: PropTypes.shape({

		id: PropTypes.string.isRequired,

		defaultMessage: PropTypes.string.isRequired,

	}),

	/**
	 * A custom react-intl message object (if any) to display as the label
	 * for the submit button.
	 */
	submitLabel: PropTypes.shape({

		id: PropTypes.string.isRequired,

		defaultMessage: PropTypes.string.isRequired,

	}),

	/**
	 * A custom react-intl message object (if any) to display as the label
	 * for the submit button on smaller devices.
	 */
	submitLabelShort: PropTypes.shape({

		id: PropTypes.string.isRequired,

		defaultMessage: PropTypes.string.isRequired,

	}),

	/**
	 * The custom warning text (if any) to display.
	 */
	warning: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

	/**
	 * The custom body text (if any) to display.
	 */
	body: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

	/**
	 * The size of the modal.
	 */
	size: PropTypes.oneOf(['sm', 'lg']),

	/**
	 * True, if the modal is visible.
	 */
	isOpen: PropTypes.bool,

	/**
	 * True, if the warning alert banner is visible.
	 */
	showWarningAlert: PropTypes.bool,

	/**
	 * True, if the confirmation checkbox should be included.
	 */
	requireConfirmation: PropTypes.bool,

	/**
	 * Callback function to handle the user cancelling the delete confirmation modal.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * Callback function to handle the user submitting the delete confirmation modal.
	 */
	onSubmit: PropTypes.func.isRequired,

};

const defaultProps = {
	submitLabel: messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODEL_SUBMIT_BUTTON_TEXT_LONG,
	submitLabelShort: messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODEL_SUBMIT_BUTTON_TEXT_SHORT,
	headerLabel: messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODEL_HEADER_TEXT,
	isOpen: false,
	showWarningAlert: true,
	requireConfirmation: true,
};

const DeleteConfirmationModal = (props) => {

	const {
		type,
		title,
		warning,
		body,
		size,
		headerLabel,
		submitLabel,
		submitLabelShort,
		showWarningAlert,
		requireConfirmation,
		isOpen,
		onCancel,
		onSubmit,
		...restProps
	} = props;

	const [confirmation, setConfirmation] = useState(!requireConfirmation);

	const [error, setError] = useState(null);

	const intl = useIntl();

	const handleSubmit = () => {

		setError(null);

		onSubmit().catch((err) => {

			setError(err);

		});

	};

	return (
		<Modal
			{...restProps}
			size={size}
			isOpen={isOpen}
			toggle={onCancel}>
			<ModalBody>
				{error &&
					<DangerAlert>
						{title
							? (
								<FormattedMessage
									{...messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODAL_ERROR_MSG_TITLE}
									values={{ title }} />
							) : (
								<FormattedMessage
									{...messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODAL_ERROR_MSG_TYPE}
									values={{ type }} />
							)
						}
					</DangerAlert>
				}
				{headerLabel &&
					<h2 className="font-weight-bold">
						<FormattedMessage
							{...headerLabel}
							values={{ title }} />
					</h2>
				}
				{(showWarningAlert || warning || body || requireConfirmation) &&
					<Form className="delete-confirmation-form pt-3 pb-3">
						{showWarningAlert &&
							<WarningAlert dismissible={false}>
								{' '}
								<FormattedMessage {...messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODEL_INFO_TEXT} />
							</WarningAlert>
						}
						{warning &&
							<FormGroup>
								{warning}
							</FormGroup>
						}
						{body}
						{requireConfirmation &&
							<FormField
								id="confirm-deletion"
								type={FIELD_TYPES.CHECKBOX}
								name="confirmation"
								label={intl.formatMessage(
									messages.TC_COMPONENTS_DELETE_CONFIRMATION_MODEL_FIELD_LABEL_TEXT_CONFIRMATION
								)}
								value={confirmation}
								isEditing
								isRequired
								isLabelInline
								onChange={(name, value) => setConfirmation(value)} />
						}
					</Form>
				}
			</ModalBody>
			<ModalFooter>
				<Button
					color="danger"
					disabled={!confirmation}
					onClick={handleSubmit}>
					{/* Show the "longer" text delete button on md, lg, xl devices */}
					<div className="d-none d-md-block">
						<FormattedMessage
							{...submitLabel}
							values={{ type }} />
					</div>
					{/* Show the "shorter" text delete button on xs,sm devices */}
					<div className="d-block d-md-none">
						<FormattedMessage
							{...submitLabelShort}
							values={{ type }} />
					</div>
				</Button>
				<Button
					color="neutral"
					outline
					onClick={onCancel}>
					<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
				</Button>
			</ModalFooter>
		</Modal>
	);

};

DeleteConfirmationModal.propTypes = propTypes;
DeleteConfirmationModal.defaultProps = defaultProps;

export default DeleteConfirmationModal;
